import * as React from 'react';

import { requestPost } from '../../../../modules/request/index';
import { Checkbox, DropdownInput, EXTRA_SMALL_ICON_BASE_SRC } from '../../../Shared/index';
import useModalAccessibility from '../../../Shared/hooks/useModalAccessibility'

const swapVerticalSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/swap-vertical-green.svg`;

const labels = {
  checkboxOne: "I understand that all data from the account to merge will be transferred to the primary account.",
  checkboxTwo: "I understand that the account to merge will be removed from the class roster.",
  checkboxThree: "I understand that this action cannot be undone.",
};

type CheckboxNames = 'checkboxOne' | 'checkboxTwo' | 'checkboxThree';

interface MergeStudentAccountsModalProps {
  classroom: any;
  close: () => void;
  onSuccess: (message: string) => void;
  selectedStudentIds: any;
}

interface MergeStudentAccountsModalState {
  checkboxOne?: boolean;
  checkboxThree?: boolean;
  checkboxTwo?: boolean;
  primaryAccountId: string;
  secondaryAccountId: string;
}

const MergeStudentAccountsModal = ({ classroom, close, onSuccess, selectedStudentIds }: MergeStudentAccountsModalProps) => {
  const { modalRef, } = useModalAccessibility(close)

  const [state, setState] = React.useState<MergeStudentAccountsModalState>({
    checkboxOne: false,
    checkboxTwo: false,
    checkboxThree: false,
    primaryAccountId: selectedStudentIds[0],
    secondaryAccountId: selectedStudentIds[1],
  });

  const studentOptions = () => {
    const { students } = classroom;
    return students.map((student: any) => {
      const completedActivityCount = student.number_of_completed_activities;
      const labelText = `${student.name} (${completedActivityCount} ${completedActivityCount === 1 ? 'activity' : 'activities'})`;
      return {
        label: labelText,
        value: student.id,
      };
    });
  };

  const handlePrimaryAccountIdChange = (option: any) => {
    setState((prevState) => ({ ...prevState, primaryAccountId: option.value }));
  };

  const handleSecondaryAccountIdChange = (option: any) => {
    setState((prevState) => ({ ...prevState, secondaryAccountId: option.value }));
  };

  const swapAccounts = () => {
    setState((prevState) => ({
      ...prevState,
      primaryAccountId: prevState.secondaryAccountId,
      secondaryAccountId: prevState.primaryAccountId,
    }));
  };

  const mergeStudentAccounts = () => {
    requestPost(
      `/teachers/classrooms/${classroom.id}/students/merge_student_accounts`,
      {
        primary_account_id: state.primaryAccountId,
        secondary_account_id: state.secondaryAccountId,
      },
      (body) => {
        onSuccess('Students merged');
        close();
      }
    );
  };

  const submitButtonClass = () => {
    const { checkboxOne, checkboxTwo, checkboxThree, primaryAccountId, secondaryAccountId } = state;
    let buttonClass = 'quill-button contained green medium focus-on-light';
    if (!(primaryAccountId && secondaryAccountId && checkboxOne && checkboxTwo && checkboxThree)) {
      buttonClass += ' disabled';
    }
    return buttonClass;
  };

  const toggleCheckbox = (checkboxNumber: CheckboxNames) => {
    setState((prevState) => ({ ...prevState, [checkboxNumber]: !prevState[checkboxNumber] }));
  };

  const renderCheckbox = (checkboxNumber: CheckboxNames) => {
    const checkbox = state[checkboxNumber];
    return (
      <Checkbox
        label={labels[checkboxNumber]}
        onClick={() => toggleCheckbox(checkboxNumber)}
        selected={checkbox}
      />
    );
  };

  const renderCheckboxes = () => (
    <div className="checkboxes">
      {renderCheckbox('checkboxOne')}
      {renderCheckbox('checkboxTwo')}
      {renderCheckbox('checkboxThree')}
    </div>
  );

  const options = studentOptions();
  const studentOptionsForPrimary = options.filter((opt) => opt.value !== state.secondaryAccountId);
  const studentOptionsForSecondary = options.filter((opt) => opt.value !== state.primaryAccountId);

  return (
    <div className="modal-container merge-student-accounts-modal-container">
      <div className="modal-background" />
      <div
        aria-labelledby="mergeStudentAccountsModalTitle"
        aria-modal="true"
        className="merge-student-accounts-modal quill-modal modal-body"
        ref={modalRef}
        role="dialog"
        tabIndex={-1}
      >
        <div>
          <h3 className="title" id="mergeStudentAccountsModalTitle">
            Merge student accounts
          </h3>
        </div>
        <p>
          Please select the account that you'd like the student to use going forward as the primary account.
        </p>
        <button
          className="quill-button swap outlined green medium icon focus-on-light"
          onClick={swapAccounts}
        >
          <img alt="Swap accounts icon" src={swapVerticalSrc} />
          Swap accounts
        </button>
        <DropdownInput
          className="large bordered primary-account focus-on-light"
          handleChange={handlePrimaryAccountIdChange}
          id="primary-account"
          label="Select primary account"
          options={studentOptionsForPrimary}
          value={options.find((so) => so.value === state.primaryAccountId)}
        />
        <DropdownInput
          className="large bordered secondary-account focus-on-light"
          handleChange={handleSecondaryAccountIdChange}
          id="secondary-account"
          label="Select account to merge"
          options={studentOptionsForSecondary}
          value={options.find((so) => so.value === state.secondaryAccountId)}
        />
        {renderCheckboxes()}
        <div className="form-buttons">
          <button
            className="quill-button outlined green medium focus-on-light"
            onClick={close}
          >
            Cancel
          </button>
          <button
            className={submitButtonClass()}
            onClick={mergeStudentAccounts}
          >
            Merge accounts
          </button>
        </div>
      </div>
    </div>
  );
};

export default MergeStudentAccountsModal;
